'use client';

import styled from 'styled-components';

import type { CSSProperties, MouseEventHandler } from 'react';
import { footprintMargin } from './footprint';
import { starfishCssUrl } from './starfish';

import type { Footprint } from './footprint';
import type { StarfishCategory, StarfishIconsByCategory } from './starfishManifest';

type Props<C extends StarfishCategory> = {
  category: C;
  name: StarfishIconsByCategory[C];
  size: number;
  /** The layout box to keep, when it differs from `size` (see {@link footprintMargin}). */
  footprint?: Footprint;
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
};

/**
 * An `<i>` rather than a `<span>`: plenty of parent styles here size or pad every `span` they
 * contain, and an icon must not pick those up.
 */
export const IconGlyph = styled.i<{ $size: number; $footprint?: Footprint }>`
  display: inline-block;
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  ${({ $size, $footprint }) => ($footprint === undefined ? '' : `margin: ${footprintMargin($size, $footprint)};`)}
  background-color: currentColor;
  mask-image: var(--starfish-mask);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 100% 100%;
  -webkit-mask-image: var(--starfish-mask);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: 100% 100%;
`;

/**
 * A Starfish icon painted in the text colour around it, so it follows its surface's state (a
 * selected pill, a hovered button, a disabled row) — an `<img>` cannot: its colour is fixed in
 * the request. A mask reads only the artwork's alpha, so `color` on any ancestor paints it. The
 * plain `.svg` is requested because a mask wants the vector, and the delivery answers it with
 * `Access-Control-Allow-Origin: *`, which a cross-origin mask needs. Use {@link Icon} instead where
 * the colour is fixed.
 */
export const MaskIcon = <C extends StarfishCategory>({
  category,
  name,
  size,
  footprint,
  className,
  onClick,
}: Props<C>) => {
  const style = { '--starfish-mask': `url('${starfishCssUrl(category, name)}')` } as CSSProperties;

  return (
    <IconGlyph
      aria-hidden="true"
      data-icon={`${category}/${name}`}
      className={className}
      onClick={onClick}
      $size={size}
      $footprint={footprint}
      style={style}
    />
  );
};
